import { Button } from '@/components/Button.tsx'
import { IconCheck, IconFrame, IconLock, IconPrinter, IconTrendingUp } from '@tabler/icons-react'
import Lightbox from 'yet-another-react-lightbox'
import { Inline } from 'yet-another-react-lightbox/plugins'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'

const PROF_PRICE = 19.99
const NORMAL_PRICE = 49.99
const RECOMMENDED_RESELL = 39.99

const productImages = [
  '/productImages/so-sieht-eure-fotogalerie-aus.webp',
  '/productImages/anpassbare-kartendesigns.webp',
  '/productImages/fotoapp-farben-beispiele.jpg',
  '/productImages/decke-alle-events-mit-ordern-ab.webp',
  '/productImages/auf-allen-geraeten-verfuegbar.webp',
]

const productLightboxSlides = productImages.map(src => ({ src }))

const cardPlaceholders = [
  { id: 1, color: '#E8DDD0', label: 'Vorlage 1' },
  { id: 2, color: '#D4C5B0', label: 'Vorlage 2' },
  { id: 3, color: '#C0A37C', label: 'Vorlage 3' },
  { id: 4, color: '#A89078', label: 'Vorlage 4' },
  { id: 5, color: '#E5DAD0', label: 'Vorlage 5' },
  { id: 6, color: '#CFC0A8', label: 'Vorlage 6' },
  { id: 7, color: '#B89F82', label: 'Vorlage 7' },
  { id: 8, color: '#D9C9B6', label: 'Vorlage 8' },
]

function formatEuro(value: number): string {
  return value.toFixed(2).replace('.', ',') + ' €'
}

interface FeatureItemProps {
  icon: React.ReactNode
  title: string
  description: string
}

function FeatureItem({ icon, title, description }: FeatureItemProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary bg-opacity-10 text-primary flex-shrink-0">
        {icon}
      </div>
      <div>
        <h4 className="font-semibold">{title}</h4>
        <p className="text-sm opacity-70">{description}</p>
      </div>
    </div>
  )
}

const featureChecks = [
  'Unbegrenzt Bilder & Videos speichern',
  'Unbegrenzt Massendownloads',
  'Du bestimmst Zugriffsrechte der Gäste',
  'Einmal kaufen, mehrere Tage verwenden',
  'Fotospiele, Live-Diashow & Gästebuch',
  'Keine Gäste-Accounts / App erforderlich',
  '25+ Sprachen',
  '12 Monate Speicherdauer (verlängerbar)',
]

function CheckRow({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <IconCheck size={16} className="text-success flex-shrink-0 mt-0.5" />
      <span>{text}</span>
    </li>
  )
}

export default function ProjectGuestGalleryTab() {
  const margin = RECOMMENDED_RESELL - PROF_PRICE

  return (
    <div className="flex flex-col gap-6">
      {/* Hero / Locked state */}
      <div className="bg-neutral border border-[var(--portal-border)] p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-60 mb-3">
              <IconLock size={14} />
              Upgrade benötigt
            </div>
            <h2 className="font-serif text-3xl md:text-4xl mb-3">Gäste-Galerie</h2>
            <p className="text-base opacity-80 mb-6">
              Lass deine Kunden Fotos und Videos ihrer Gäste sammeln - ganz einfach per QR-Code.
              Powered by <span className="font-semibold">FridaySnap</span>, unserem Hauptprodukt für
              gemeinsame Event-Galerien.
            </p>
            <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row flex-wrap gap-3">
              <Button color="primary">
                Upgrade für dieses Projekt freischalten
              </Button>
              <Button variant="outline" color="ghost">
                Mehr erfahren
              </Button>
            </div>
          </div>

          <div className="product-lightbox-compact w-full">
            <Lightbox
              plugins={[Inline, Thumbnails]}
              slides={productLightboxSlides}
              carousel={{ finite: true }}
              thumbnails={{ vignette: false, position: 'bottom', width: 80, height: 80 }}
            />
          </div>
        </div>
      </div>

      {/* Pricing / Margin */}
      <div className="bg-neutral border border-[var(--portal-border)] p-6 md:p-8">
        <h3 className="font-semibold text-lg mb-1">Vergünstigt für Profis - verkauf weiter mit Marge</h3>
        <p className="text-sm opacity-60 mb-6">
          Du bekommst die Gäste-Galerie zum reduzierten Preis und kannst sie als Aufpreis-Paket an
          deine Kunden weitergeben.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-secondary p-5 rounded-lg">
            <div className="text-xs uppercase tracking-wider opacity-60 mb-2">Dein Einkauf</div>
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-serif">{formatEuro(PROF_PRICE)}</div>
              <div className="text-sm opacity-50 line-through">{formatEuro(NORMAL_PRICE)}</div>
            </div>
            <div className="text-xs opacity-60 mt-1">pro Projekt - Profi-Rabatt inklusive</div>
          </div>

          <div className="bg-secondary p-5 rounded-lg">
            <div className="text-xs uppercase tracking-wider opacity-60 mb-2">Empfohlener Verkaufspreis</div>
            <div className="text-3xl font-serif">{formatEuro(RECOMMENDED_RESELL)}</div>
            <div className="text-xs opacity-60 mt-1">an deinen Kunden</div>
          </div>

          <div className="bg-primary bg-opacity-10 p-5 rounded-lg border border-primary border-opacity-20">
            <div className="text-xs uppercase tracking-wider text-primary mb-2 flex items-center gap-1">
              <IconTrendingUp size={14} />
              Deine Marge
            </div>
            <div className="text-3xl font-serif text-primary">{formatEuro(margin)}</div>
            <div className="text-xs opacity-60 mt-1">pro verkauftem Paket</div>
          </div>
        </div>

        <p className="text-xs opacity-50 mt-4">
          Verkaufspreis frei wählbar - der Wert hier ist nur eine Empfehlung.
        </p>
      </div>

      {/* Features */}
      <div className="bg-neutral border border-[var(--portal-border)] p-6 md:p-8">
        <h3 className="font-semibold text-lg mb-3">Was die Gäste-Galerie kann</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="bg-primary text-neutral text-xs font-semibold px-3 py-1 rounded-full">
            10.000+ Hochzeiten
          </span>
          <span className="bg-primary text-neutral text-xs font-semibold px-3 py-1 rounded-full">
            400.000+ Nutzer
          </span>
        </div>
        <p className="text-sm opacity-70 mb-5 max-w-2xl">
          Gäste scannen einen QR-Code - keine E-Mail, kein App-Download. Sie laden Fotos &
          Videos sofort oder später hoch. Du entscheidest, ob sie auch Zugriff auf die Galerie
          bekommen oder nur hochladen dürfen.
        </p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {featureChecks.map(f => <CheckRow key={f} text={f} />)}
        </ul>
      </div>

      {/* How it works */}
      <div className="bg-neutral border border-[var(--portal-border)] p-6 md:p-8">
        <h3 className="font-semibold text-lg mb-6">So funktioniert's</h3>
        <ol className="flex flex-col gap-4">
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-primary text-neutral flex items-center justify-center text-sm font-semibold flex-shrink-0">1</div>
            <div>
              <div className="font-medium">Upgrade für dieses Projekt freischalten</div>
              <div className="text-sm opacity-70">Einmalig {formatEuro(PROF_PRICE)}.</div>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-primary text-neutral flex items-center justify-center text-sm font-semibold flex-shrink-0">2</div>
            <div>
              <div className="font-medium">Verkauf an deinen Kunden</div>
              <div className="text-sm opacity-70">Biete die Gäste-Galerie als Zusatzpaket - z.B. für {formatEuro(RECOMMENDED_RESELL)}.</div>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-primary text-neutral flex items-center justify-center text-sm font-semibold flex-shrink-0">3</div>
            <div>
              <div className="font-medium">Kunde teilt QR-Code mit Gästen</div>
              <div className="text-sm opacity-70">Gäste laden Fotos hoch, alles fließt automatisch in die Galerie.</div>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-primary text-neutral flex items-center justify-center text-sm font-semibold flex-shrink-0">4</div>
            <div>
              <div className="font-medium">Du behältst die Marge</div>
              <div className="text-sm opacity-70 flex items-center gap-1">
                <IconCheck size={14} className="text-success" />
                {formatEuro(margin)} Gewinn pro verkauftem Paket bei empfohlenem Verkaufspreis.
              </div>
            </div>
          </li>
        </ol>
      </div>

      {/* Karten & Drucke */}
      <div className="bg-neutral border border-[var(--portal-border)] p-6 md:p-8">
        <h3 className="font-semibold text-lg mb-2">Karten, Drucke & Bilderrahmen</h3>
        <p className="text-sm opacity-70 mb-6 max-w-2xl">
          Zur Gäste-Galerie gibt's passende Kartenvorlagen mit QR-Code - kostenlos zum
          Selberdrucken oder direkt über uns drucken lassen. Dazu Bilderrahmen, perfekt abgestimmt
          auf das Kartenformat.
        </p>

        <div
          className="scroller mb-6"
          data-animated="true"
          data-direction="left"
          data-speed="slow"
        >
          <div className="scroller__inner">
            {[...cardPlaceholders, ...cardPlaceholders].map((card, i) => (
              <div
                key={`${card.id}-${i}`}
                className="w-[10rem] aspect-[3/4] rounded-lg flex-shrink-0 flex items-center justify-center text-xs opacity-60 shadow-sm"
                style={{ backgroundColor: card.color }}
              >
                {card.label}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FeatureItem
            icon={<IconPrinter size={20} />}
            title="Karten drucken lassen"
            description="Vorlagen kostenlos herunterladen oder direkt bei uns drucken - inkl. QR-Code zur Gäste-Galerie."
          />
          <FeatureItem
            icon={<IconFrame size={20} />}
            title="Passende Bilderrahmen"
            description="Hochwertige Rahmen, abgestimmt auf das Kartenformat. Direkt an deinen Kunden lieferbar."
          />
        </div>
      </div>

      <div className="flex justify-center">
        <Button color="primary" size="lg">
          Jetzt für dieses Projekt freischalten - {formatEuro(PROF_PRICE)}
        </Button>
      </div>
    </div>
  )
}
